import React, { useState, useEffect } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { INR, fmtDate, titleCase, StatusBadge } from "@/lib/format";
import { PageHeader, DataTable, Pager, useList } from "@/components/shared";
import { FormDialog } from "@/components/EntityForm";
import { Button } from "@/components/ui/button";
import { FileSpreadsheet, Plus, FileDown, Mail, Loader2 } from "lucide-react";

export default function Quotes() {
  const { hasPerm } = useAuth();
  const [insurers, setInsurers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const { items, total, loading, page, setPage, refetch } = useList("/quotes", {});
  useEffect(() => {
    api.get("/insurers").then(({ data }) => setInsurers(data)).catch(() => {});
    api.get("/customers", { params: { limit: 100 } }).then(({ data }) => setCustomers(data.items || [])).catch(() => {});
  }, []);

  const create = async (v) => { await api.post("/quotes", v); refetch(); };
  const [sending, setSending] = useState(null);
  const downloadPdf = async (q) => {
    try {
      const { data } = await api.get(`/quotes/${q.id}/pdf`, { responseType: "blob" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(data); a.download = `${q.quote_number}.pdf`; a.click();
    } catch (ex) { toast.error("Could not download this quote"); }
  };
  const emailQuote = async (q) => {
    if (!window.confirm(`Email quote ${q.quote_number} to ${custName(q.customer_id)}?`)) return;
    setSending(q.id);
    try { const { data } = await api.post(`/quotes/${q.id}/email`); toast.success(`Quote emailed to ${data.sent_to}`); }
    catch (ex) { toast.error(apiError(ex.response?.data?.detail)); } finally { setSending(null); }
  };
  const fields = [
    { name: "customer_id", label: "Customer", type: "select", required: true, options: customers.map((x) => ({ value: x.id, label: `${x.name} (${x.customer_id})` })) },
    { name: "insurer", label: "Insurer", type: "select", required: true, options: insurers.map((x) => ({ value: x.name, label: x.name })) },
    { name: "insurance_type", label: "Insurance Type", type: "select", options: ["Car Insurance", "Bike Insurance", "Health Insurance"] },
    { name: "plan", label: "Plan / Coverage" },
    { name: "idv", label: "IDV / Sum Insured (₹)", type: "number" },
    { name: "premium", label: "Premium (₹)", type: "number", required: true },
    { name: "add_ons", label: "Add-ons", placeholder: "Zero Dep, Engine Protect" },
    { name: "quote_validity", label: "Valid Till", type: "date" },
    { name: "quote_source", label: "Source", type: "select", options: [{ value: "manual", label: "Manual Estimate" }, { value: "api", label: "Live API Quote" }] },
  ];
  const custName = (id) => customers.find((c) => c.id === id)?.name || "—";
  const columns = [
    { key: "quote_number", header: "Quote No", render: (r) => <span className="font-mono text-xs">{r.quote_number}</span> },
    { key: "customer", header: "Customer", render: (r) => <span className="text-sm">{custName(r.customer_id)}</span> },
    { key: "insurer", header: "Insurer", render: (r) => <span className="font-semibold">{r.insurer}</span> },
    { key: "plan", header: "Plan", render: (r) => <span className="text-sm text-muted-foreground">{r.plan || r.insurance_type || "—"}</span> },
    { key: "premium", header: "Premium", tdClass: "text-right", thClass: "text-right", render: (r) => <span className="tabular font-semibold">{INR(r.premium)}</span> },
    { key: "quote_source", header: "Source", render: (r) => <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-muted">{titleCase(r.quote_source || "manual")}</span> },
    { key: "status", header: "Status", render: (r) => <StatusBadge value={r.status} /> },
    { key: "created_at", header: "Date", render: (r) => <span className="text-sm">{fmtDate(r.created_at)}</span> },
    { key: "share", header: "", render: (r) => (
      <div className="flex gap-1">
        <Button size="icon" variant="ghost" className="h-8 w-8" title="Download PDF" onClick={() => downloadPdf(r)} data-testid="quote-pdf-btn"><FileDown className="h-4 w-4" /></Button>
        {hasPerm("communications:add") && <Button size="icon" variant="ghost" className="h-8 w-8 text-primary" title="Email quote to customer" disabled={sending === r.id} onClick={() => emailQuote(r)} data-testid="quote-email-btn">
          {sending === r.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}</Button>}
      </div>
    ) },
  ];
  return (
    <div>
      <PageHeader title="Quotes" subtitle={`${total} quotes · live API quotes labeled separately`} icon={FileSpreadsheet}
        actions={hasPerm("quotes:add") && (
          <FormDialog title="New Quote" fields={fields} onSubmit={create} submitLabel="Generate Quote" testId="create-quote-dialog"
            trigger={<Button data-testid="create-quote-btn"><Plus className="h-4 w-4 mr-1.5" /> New Quote</Button>} />
        )} />
      <DataTable columns={columns} rows={items} loading={loading} rowTestId="quote-table-row" />
      <Pager page={page} limit={25} total={total} onPage={setPage} />
    </div>
  );
}
